/**
 * Round 58 - finds the avatar openings of a frame image from its transparency, and holds the metadata types the Frame Editor
 * edits and the app reads. Pure functions (no DOM), so they run in the editor, in tests, and could run on a server.
 *
 * What counts as an opening: a transparent region that is completely ENCLOSED by opaque pixels (it does not touch the image
 * border), is big enough, and is round-ish or square-ish. Every other transparent gap (between wings, around hearts, the margin
 * outside the artwork) is decoration and is ignored. Detection is a starting point only: decorative art can fool it, so the
 * editor always lets an admin move/resize/add/remove openings before saving.
 */

export type SlotShape = "circle" | "oval" | "rounded" | "square";

/** Coordinates are in the frame image's own pixels (origin top-left). cx/cy = centre, w/h = size. */
export interface OpeningMeta { id: string; cx: number; cy: number; w: number; h: number; shape: SlotShape }
export interface LabelMeta { id: string; cx: number; cy: number; w: number; h: number }
export interface FrameMeta {
  frameKey: string;
  imageUrl?: string | null;
  canvasW: number;
  canvasH: number;
  openings: OpeningMeta[];
  labels: LabelMeta[];
  source?: "auto" | "admin";
}

export interface Candidate {
  cx: number; cy: number; w: number; h: number; shape: SlotShape;
  area: number; fill: number; aspect: number;
  likely: boolean;      // looks like an avatar opening
  reason: string;       // why it was (not) treated as one - shown to the admin
}

export interface DetectResult { threshold: number; candidates: Candidate[] }

const THRESHOLDS = [32, 128, 200];

/** rgba: canvas ImageData.data (4 bytes per pixel). */
export function detectOpenings(rgba: ArrayLike<number>, width: number, height: number): DetectResult {
  let best: DetectResult | null = null;
  for (const thr of THRESHOLDS) {
    const res = analyse(rgba, width, height, thr);
    if (!best) best = res;
    if (res.candidates.some((c) => c.likely)) return res;   // first threshold that yields a real opening wins
  }
  return best as DetectResult;
}

function analyse(rgba: ArrayLike<number>, w: number, h: number, thr: number): DetectResult {
  const n = w * h;
  const label = new Int32Array(n);              // 0 = not transparent / unvisited
  const stack = new Int32Array(n);
  const total = n;
  const comps: Array<{ area: number; x0: number; y0: number; x1: number; y1: number; border: boolean }> = [];
  for (let start = 0; start < n; start++) {
    if (label[start] !== 0 || rgba[start * 4 + 3] >= thr) continue;
    const id = comps.length + 1;
    let sp = 0, area = 0, x0 = w, y0 = h, x1 = 0, y1 = 0, border = false;
    stack[sp++] = start; label[start] = id;
    while (sp > 0) {
      const p = stack[--sp];
      const x = p % w, y = (p / w) | 0;
      area++;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) border = true;
      if (x > 0) { const q = p - 1; if (label[q] === 0 && rgba[q * 4 + 3] < thr) { label[q] = id; stack[sp++] = q; } }
      if (x < w - 1) { const q = p + 1; if (label[q] === 0 && rgba[q * 4 + 3] < thr) { label[q] = id; stack[sp++] = q; } }
      if (y > 0) { const q = p - w; if (label[q] === 0 && rgba[q * 4 + 3] < thr) { label[q] = id; stack[sp++] = q; } }
      if (y < h - 1) { const q = p + w; if (label[q] === 0 && rgba[q * 4 + 3] < thr) { label[q] = id; stack[sp++] = q; } }
    }
    comps.push({ area, x0, y0, x1, y1, border });
  }

  const enclosed = comps.filter((c) => !c.border && c.area >= total * 0.002);
  const biggest = enclosed.reduce((m, c) => Math.max(m, c.area), 0);
  const candidates: Candidate[] = enclosed.map((c) => {
    const bw = c.x1 - c.x0 + 1, bh = c.y1 - c.y0 + 1;
    const fill = c.area / (bw * bh);
    const aspect = Math.min(bw, bh) / Math.max(bw, bh);
    let shape: SlotShape = "oval";
    if (fill >= 0.93) shape = "rounded";
    else if (aspect >= 0.85) shape = "circle";
    const roundish = fill >= 0.7 && aspect >= 0.6;            // ellipse fills 0.785 of its box, a rounded square more
    const bigEnough = c.area >= total * 0.01 && c.area >= biggest * 0.4;
    const likely = roundish && bigEnough;
    const reason = likely ? "enclosed, round, large"
      : !roundish ? "irregular shape (decoration gap?)"
      : "too small compared with the main opening";
    const side = Math.max(bw, bh);
    return {
      cx: round1((c.x0 + c.x1 + 1) / 2), cy: round1((c.y0 + c.y1 + 1) / 2),
      w: shape === "circle" ? side : bw, h: shape === "circle" ? side : bh,
      shape, area: c.area, fill: round3(fill), aspect: round3(aspect), likely, reason,
    };
  });
  candidates.sort((a, b) => a.cx - b.cx || a.cy - b.cy);
  return { threshold: thr, candidates };
}

const round1 = (v: number) => Math.round(v * 10) / 10;
const round3 = (v: number) => Math.round(v * 1000) / 1000;

/** Turns the likely candidates into opening metadata: left-to-right ids "left"/"right" for two, "main" for one, "o1".."oN" otherwise. */
export function candidatesToOpenings(cands: Candidate[]): OpeningMeta[] {
  const likely = cands.filter((c) => c.likely);
  const ids = likely.length === 1 ? ["main"] : likely.length === 2 ? ["left", "right"] : likely.map((_, i) => `o${i + 1}`);
  return likely.map((c, i) => ({ id: ids[i], cx: c.cx, cy: c.cy, w: c.w, h: c.h, shape: c.shape }));
}

/** Where a slot sits when the frame is drawn at displayW x displayH (aspect preserved, centred) - the same maths as the Android view. */
export function scaleSlot(s: { cx: number; cy: number; w: number; h: number }, canvasW: number, canvasH: number, displayW: number, displayH: number) {
  const k = Math.min(displayW / canvasW, displayH / canvasH);
  const offX = (displayW - canvasW * k) / 2, offY = (displayH - canvasH * k) / 2;
  return { x: offX + (s.cx - s.w / 2) * k, y: offY + (s.cy - s.h / 2) * k, w: s.w * k, h: s.h * k, scale: k };
}
